import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import './RemindersPage.css';

const RemindersPage = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming', 'completed', 'all'
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchReminders();
  }, [currentUser, filter]);

  const fetchReminders = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      
      // Base query
      let remindersQuery;
      
   // 10.09.25 10:15 AEST - Ultra-simple query to test basic functionality
   if (filter === 'upcoming') {
    remindersQuery = query(
      collection(db, 'Users', currentUser.email, 'Notifications'),
      where('type', '==', 'reminder')
    );
  // 10.09.25 10:15 AEST - Ultra-simple completed query  
} else if (filter === 'completed') {
  remindersQuery = query(
    collection(db, 'Users', currentUser.email, 'Notifications'),
    where('type', '==', 'reminder')
  );
} else {
  // 10.09.25 10:15 AEST - Ultra-simple all query
  remindersQuery = query(
    collection(db, 'Users', currentUser.email, 'Notifications'),
    where('type', '==', 'reminder')
  );
      }
      
      const querySnapshot = await getDocs(remindersQuery);
      const remindersList = [];
      
      querySnapshot.forEach(doc => {
        remindersList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setReminders(remindersList);
    } catch (err) {
      console.error('Error fetching reminders:', err);
      setError('Failed to load reminders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (reminderId, isCompleted) => {
    if (!currentUser) return;
    
    try {
      const reminderRef = doc(
        db, 
        'Users', 
        currentUser.email, 
        'Notifications', 
        reminderId
      );
      
      await updateDoc(reminderRef, {
        isCompleted: !isCompleted,
        completedAt: !isCompleted ? Timestamp.now() : null
      });
      
      // Update local state
      setReminders(prevReminders => 
        prevReminders.map(reminder => 
          reminder.id === reminderId 
            ? { ...reminder, isCompleted: !isCompleted, completedAt: !isCompleted ? Timestamp.now() : null } 
            : reminder
        )
      );
    } catch (err) {
      console.error('Error updating reminder:', err);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      
      // Check if today
      if (date.setHours(0, 0, 0, 0) === now.setHours(0, 0, 0, 0)) {
        return 'Today';
      }
      
      // Check if tomorrow
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (date.setHours(0, 0, 0, 0) === tomorrow.setHours(0, 0, 0, 0)) {
        return 'Tomorrow';
      }
      
      // Regular date
      return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };
  
  // Determine reminder status for visual styling
  const getReminderStatus = (reminder) => {
    if (reminder.isCompleted) {
      return 'completed';
    }
    
    if (!reminder.dueDate) return 'unknown';
    
    try {
      const dueDate = reminder.dueDate.toDate();
      const now = new Date();
      
      if (dueDate < now) {
        return 'overdue';
      }
      
      const diffDays = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
      
      if (diffDays <= 7) {
        return 'soon';
      } else if (diffDays <= 30) {
        return 'upcoming';
      } else {
        return 'future';
      }
    } catch (err) {
      return 'unknown';
    }
  };

  return (
    <div className="reminders-page">
      <header className="page-header">
        <h1>Reminders</h1>
      </header>
      
      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filter === 'upcoming' ? 'active' : ''}`}
          onClick={() => setFilter('upcoming')}
        >
          Upcoming
        </button>
        <button 
          className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading reminders...</p>
        </div>
      ) : reminders.length === 0 ? (
        <div className="empty-state">
          <span className="material-icons empty-state-icon">notifications</span>
          <h2>No reminders found</h2>
          <p>
            {filter === 'upcoming' 
              ? "You don't have any upcoming reminders" 
              : filter === 'completed' 
              ? "You haven't completed any reminders yet" 
              : "No reminders found"}
          </p>
        </div>
      ) : (
        <div className="reminders-timeline">
          {reminders.map(reminder => {
            const status = getReminderStatus(reminder);
            
            return (
              <div 
                key={reminder.id} 
                className={`reminder-card ${status}`}
              >
                <div className="reminder-checkbox">
                  <button 
                    className={`checkbox ${reminder.isCompleted ? 'checked' : ''}`}
                    onClick={() => handleToggleComplete(reminder.id, reminder.isCompleted)}
                  >
                    {reminder.isCompleted && <span className="material-icons">check</span>}
                  </button>
                </div>
                
                <div className="reminder-content">
                  <div className="reminder-header">
                    <h3 className="reminder-title">{reminder.title || reminder.message}</h3>
                    <div className="reminder-date">
                      {formatDate(reminder.dueDate)}
                    </div>
                  </div>
                  
                  {reminder.message && reminder.title && (
                    <div className="reminder-message">{reminder.message}</div>
                  )}
                  
                  {reminder.fileId && (
                    <div className="reminder-document">
                      <span className="material-icons document-icon">description</span>
                      <span className="document-name">{reminder.fileName || 'Related document'}</span>
                      
                      <a 
                        href={reminder.documentUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="view-document-button"
                      >
                        View
                      </a>
                    </div>
                  )}
                  
                  {reminder.isCompleted && reminder.completedAt && (
                    <div className="completion-info">
                      Completed {formatDate(reminder.completedAt)}
                    </div>
                  )}
                </div>
                
                <div className="reminder-actions">
                  <button className="reminder-action-button">
                    <span className="material-icons">more_vert</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RemindersPage;